import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'; // useParams
//import { Layout } from './components/Layout';
import { Login } from './pages/auth/Login';
import { AccountConfirmation } from './pages/auth/AccountConfirmation';
import { ResetPassword } from './pages/auth/ResetPassword';
import { UserManagement } from './pages/UserManagement';
import { UserForm } from './components/features/users';
import { DeviceManagement } from './pages/DeviceManagement';
import { DeviceForm } from './components/features/devices';
import { MainMenu } from './components/shared/navigation';
import { Dashboard } from './pages/Dashboard';
// DashboardSimple removed - content moved to Dashboard.tsx
// DeviceOperationsPage is now imported from the operations index
import { PrivateRoute } from './components/features/auth';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ToastProvider } from './contexts/ToastContext';
import { DashboardDataProvider } from './contexts/DashboardDataContext';
import { UserDeviceManagerWrapper } from '@/components/features/users/UserDeviceManagerWrapper';
import { DeviceUserManagerWrapper } from '@/components/features/devices/DeviceUserManagerWrapper';
import { Layout } from './components/shared/layout/Layout';
import { ToastContainer } from './components/ui/ToastContainer';
import { DialogCleanupWrapper } from './components/shared/DialogCleanupWrapper';
// Operational pages
import {
  AllDeviceByUsersPage,
  DeviceOperationsPage,
  OperationLecturesPage,
  AllDeviceLecturesPage,
  AllDevicePendingLecturesPage,
  LectureInsertPage,
  BulkLectureInsertPage,
  ImportLectureFilePage,
} from './pages/operations';
import { EventTypesManagementPage, PlatformHomePage } from './pages/platform';
import { EventTypeForm } from './components/features/platform/EventTypeForm';
import { eventTypeService } from './services/api';
import { setDynamicEventTypes } from './utils/operational-ui.utils';

// import { TestToast } from './components/ui/TestToast';

const PrivateLayout = ({
  children,
  backgroundColor = 'bg-brand-white',
}: {
  children: React.ReactNode;
  backgroundColor?: string;
}) => {
  return (
    <PrivateRoute>
      <Layout backgroundColor={backgroundColor}>{children}</Layout>
    </PrivateRoute>
  );
};

function App() {
  React.useEffect(() => {
    const syncEventTypes = async () => {
      const response = await eventTypeService.getAll();
      if (response.success && response.data) {
        setDynamicEventTypes(response.data);
      }
    };
    void syncEventTypes();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <PermissionsProvider>
          <ToastProvider>
            <DialogCleanupWrapper>
              <DashboardDataProvider>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/confirm-account" element={<AccountConfirmation />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Private routes wrapped in Layout */}
                  <Route path="/" element={<Navigate to="/menu" replace />} />
                  <Route
                    path="/menu"
                    element={
                      <PrivateLayout backgroundColor="bg-gray-100">
                        <MainMenu />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <PrivateLayout>
                        <UserManagement />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/users/new"
                    element={
                      <PrivateLayout>
                        <UserForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/users/edit/:id"
                    element={
                      <PrivateLayout>
                        <UserForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/users/:id/devices"
                    element={
                      <PrivateLayout>
                        <UserDeviceManagerWrapper />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/devices"
                    element={
                      <PrivateLayout>
                        <DeviceManagement />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/devices/new"
                    element={
                      <PrivateLayout>
                        <DeviceForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/devices/edit/:id"
                    element={
                      <PrivateLayout>
                        <DeviceForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/devices/:id"
                    element={
                      <PrivateLayout>
                        <DeviceForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/devices/:id/users"
                    element={
                      <PrivateLayout>
                        <DeviceUserManagerWrapper />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateLayout>
                        <Dashboard />
                      </PrivateLayout>
                    }
                  />

                  {/* Operational routes - Original */}
                  <Route
                    path="/operations"
                    element={
                      <PrivateLayout>
                        <AllDeviceByUsersPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/operations/device/:deviceId"
                    element={
                      <PrivateLayout>
                        <DeviceOperationsPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/operations/device/:deviceId/operation/:operationId/lectures"
                    element={
                      <PrivateLayout>
                        <OperationLecturesPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/operations/device/:deviceId/all-lectures"
                    element={
                      <PrivateLayout>
                        <AllDeviceLecturesPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/operations/device/:deviceId/pending-lectures"
                    element={
                      <PrivateLayout>
                        <AllDevicePendingLecturesPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/lectures/insert"
                    element={
                      <PrivateLayout>
                        <LectureInsertPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/lectures/bulk-insert"
                    element={
                      <PrivateLayout>
                        <BulkLectureInsertPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/lectures/import"
                    element={
                      <PrivateLayout>
                        <ImportLectureFilePage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/platform"
                    element={
                      <PrivateLayout>
                        <PlatformHomePage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/platform/event-types"
                    element={
                      <PrivateLayout>
                        <EventTypesManagementPage />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/platform/event-types/new"
                    element={
                      <PrivateLayout>
                        <EventTypeForm />
                      </PrivateLayout>
                    }
                  />
                  <Route
                    path="/platform/event-types/edit/:id"
                    element={
                      <PrivateLayout>
                        <EventTypeForm />
                      </PrivateLayout>
                    }
                  />
                </Routes>
                <ToastContainer />
              </DashboardDataProvider>
            </DialogCleanupWrapper>
          </ToastProvider>
        </PermissionsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
